import { api } from "#/lib/api-client";
import type {
  ApprovalModule,
  Delegation,
  DelegationCreateRequest,
  DelegationUpdateRequest,
  OptionItem,
  Workflow,
  WorkflowCreateRequest,
  WorkflowRuleCreateRequest,
  WorkflowRuleUpdateRequest,
  WorkflowStepCreateRequest,
  WorkflowStepUpdateRequest,
  WorkflowUpdateRequest,
} from "./types";

const BASE = "/api/hr/workflows";

export async function getWorkflows(moduleCode?: string): Promise<Workflow[]> {
  const { data } = await api.get<Workflow[]>(BASE, {
    params: moduleCode ? { moduleCode } : {},
  });
  return data;
}

export async function getWorkflow(id: number): Promise<Workflow> {
  const { data } = await api.get<Workflow>(`${BASE}/${id}`);
  return data;
}

export async function createWorkflow(payload: WorkflowCreateRequest) {
  const { data } = await api.post<Workflow>(BASE, payload);
  return data;
}

export async function updateWorkflow(id: number, payload: WorkflowUpdateRequest) {
  const { data } = await api.put<Workflow>(`${BASE}/${id}`, payload);
  return data;
}

export async function deleteWorkflow(id: number) {
  await api.delete(`${BASE}/${id}`);
}

export async function publishWorkflow(id: number) {
  const { data } = await api.put<Workflow>(`${BASE}/${id}/publish`);
  return data;
}

export async function activateWorkflow(id: number) {
  const { data } = await api.put<Workflow>(`${BASE}/${id}/activate`);
  return data;
}

export async function deactivateWorkflow(id: number) {
  const { data } = await api.put<Workflow>(`${BASE}/${id}/deactivate`);
  return data;
}

export async function addWorkflowStep(
  workflowId: number,
  payload: WorkflowStepCreateRequest,
) {
  const { data } = await api.post(`${BASE}/${workflowId}/steps`, payload);
  return data;
}

export async function updateWorkflowStep(
  workflowId: number,
  stepId: number,
  payload: WorkflowStepUpdateRequest,
) {
  const { data } = await api.put(`${BASE}/${workflowId}/steps/${stepId}`, payload);
  return data;
}

export async function deleteWorkflowStep(workflowId: number, stepId: number) {
  await api.delete(`${BASE}/${workflowId}/steps/${stepId}`);
}

export async function addWorkflowRule(
  workflowId: number,
  payload: WorkflowRuleCreateRequest,
) {
  const { data } = await api.post(`${BASE}/${workflowId}/rules`, payload);
  return data;
}

export async function updateWorkflowRule(
  workflowId: number,
  ruleId: number,
  payload: WorkflowRuleUpdateRequest,
) {
  const { data } = await api.put(`${BASE}/${workflowId}/rules/${ruleId}`, payload);
  return data;
}

export async function deleteWorkflowRule(workflowId: number, ruleId: number) {
  await api.delete(`${BASE}/${workflowId}/rules/${ruleId}`);
}

export async function getDelegations(moduleCode?: ApprovalModule | "") {
  const { data } = await api.get<Delegation[]>(`${BASE}/delegations`, {
    params: moduleCode ? { moduleCode } : {},
  });
  return data;
}

export async function createDelegation(payload: DelegationCreateRequest) {
  const { data } = await api.post<Delegation>(`${BASE}/delegations`, payload);
  return data;
}

export async function updateDelegation(id: number, payload: DelegationUpdateRequest) {
  const { data } = await api.put<Delegation>(`${BASE}/delegations/${id}`, payload);
  return data;
}

export async function deleteDelegation(id: number) {
  await api.delete(`${BASE}/delegations/${id}`);
}

export async function getDepartmentOptions() {
  const { data } = await api.get<OptionItem[]>("/api/hr/departments/options");
  return data;
}

export async function getRoleOptions() {
  const { data } = await api.get<OptionItem[]>("/api/hr/roles/options");
  return data;
}

// Confirmed working as of 2026-07-08 (gated by HR_EMPLOYEE_VIEW / HR_EMPLOYEE_MANAGE /
// WORKFLOW_VIEW / SUPER_ADMIN) — a flat list, not paginated. An earlier comment here claimed
// this route didn't exist and fell back to the paginated /api/hr/users endpoint; the backend
// team has since confirmed /options is real and this is the intended dropdown source for any
// "pick a system user" field (e.g. NCR raisedByUserId/authorizedByUserId).
export async function getUserOptions(): Promise<OptionItem[]> {
  const { data } = await api.get<OptionItem[]>("/api/hr/users/options");
  return data;
}